import { BISHOP, EMPTY, KING, KNIGHT, PAWN, QUEEN, ROOK, typeOf, type Position } from '../game/types.js';
import { SQUARE_COUNT } from '../game/squares.js';
import {
  BISHOP_PST, KING_END_PST, KING_MIDDLE_PST, KNIGHT_PST, PAWN_PST, PIECE_VALUES, QUEEN_PST, ROOK_PST,
} from './pst.js';

const BISHOP_PAIR_BONUS = 30;
// Non-pawn material at the start, both sides: 4 knights, 4 bishops, 4 rooks, 2 queens.
const MINORS_AND_ROOKS_PER_TYPE = 4;
const QUEENS = 2;
const OPENING_NON_PAWN_MATERIAL =
  MINORS_AND_ROOKS_PER_TYPE * (PIECE_VALUES[KNIGHT] + PIECE_VALUES[BISHOP] + PIECE_VALUES[ROOK]) +
  QUEENS * PIECE_VALUES[QUEEN];
const MIRROR = 56;

const PST_BY_TYPE: Readonly<Record<number, readonly number[]>> = {
  [PAWN]: PAWN_PST,
  [KNIGHT]: KNIGHT_PST,
  [BISHOP]: BISHOP_PST,
  [ROOK]: ROOK_PST,
  [QUEEN]: QUEEN_PST,
};

/**
 * Static evaluation in centipawns from the side to move's point of view.
 * Material + piece-square tables, a king table tapered from middlegame to
 * endgame by remaining non-pawn material, and a bishop-pair bonus.
 */
export function evaluate(pos: Position): number {
  let score = 0;
  let nonPawn = 0;
  let whiteBishops = 0;
  let blackBishops = 0;
  let whiteKing = -1;
  let blackKing = -1;

  for (let sq = 0; sq < SQUARE_COUNT; sq++) {
    const code = pos.board[sq] ?? EMPTY;
    if (code === EMPTY) continue;
    const type = typeOf(code);
    const white = code > 0;
    if (type === KING) {
      if (white) whiteKing = sq;
      else blackKing = sq;
      continue;
    }
    const tableSq = white ? sq : sq ^ MIRROR;
    const value = (PIECE_VALUES[type] ?? 0) + (PST_BY_TYPE[type]?.[tableSq] ?? 0);
    score += white ? value : -value;
    if (type !== PAWN) nonPawn += PIECE_VALUES[type] ?? 0;
    if (type === BISHOP) {
      if (white) whiteBishops++;
      else blackBishops++;
    }
  }

  const middlegame = Math.min(1, nonPawn / OPENING_NON_PAWN_MATERIAL);
  const kingScore = (sq: number): number =>
    (KING_MIDDLE_PST[sq] ?? 0) * middlegame + (KING_END_PST[sq] ?? 0) * (1 - middlegame);
  if (whiteKing >= 0) score += kingScore(whiteKing);
  if (blackKing >= 0) score -= kingScore(blackKing ^ MIRROR);
  if (whiteBishops >= 2) score += BISHOP_PAIR_BONUS;
  if (blackBishops >= 2) score -= BISHOP_PAIR_BONUS;

  const rounded = Math.round(score);
  return pos.turn === 'w' ? rounded : -rounded;
}
